import { TextField, TextFieldProps, useTheme } from '@mui/material';
import React, { useMemo, useState } from 'react';

type NumberInputProperties = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: number | undefined;
  onChange: (value: number) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
  step?: number;
  onBlur?: () => void;
  onEnter?: () => void;
};
export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProperties>(
  ({ value, onChange, decimalPlaces, min, max, onBlur, onEnter, ...properties }, reference) => {
    const theme = useTheme();

    const [isTyping, setIsTyping] = useState(false);
    const [typingValue, setTypingValue] = useState('');

    const allowAllDecimals = decimalPlaces === undefined;
    const isDecimalPointAllowed = allowAllDecimals || decimalPlaces > 0;

    const displayValue = useMemo(() => {
      if (isTyping) {
        return typingValue;
      }
      if (value === undefined) {
        return '';
      }
      return allowAllDecimals ? value.toString() : value.toFixed(decimalPlaces);
    }, [isTyping, typingValue, allowAllDecimals, value, decimalPlaces]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newInputValue = event.target.value;

      if (min !== undefined && min > 0 && newInputValue.includes('-')) {
        return;
      }

      if (isDecimalPointAllowed && min !== undefined && min >= 1 && newInputValue.startsWith('.')) {
        return;
      }

      if (min !== undefined && min >= 1 && newInputValue.startsWith('0')) {
        return;
      }

      if (isDecimalPointAllowed) {
        const decimalPlacesCount = newInputValue.split('.')[1]?.length ?? 0;
        if (!allowAllDecimals && decimalPlacesCount > decimalPlaces) {
          return;
        }
      }

      if (max !== undefined && +newInputValue > max) {
        return;
      }

      setIsTyping(true);
      setTypingValue(newInputValue);

      if (newInputValue === '') {
        return;
      }

      const parsedValue = isDecimalPointAllowed ? Number.parseFloat(newInputValue) : Number.parseInt(newInputValue, 10);

      if (!Number.isNaN(parsedValue)) {
        if (min !== undefined && parsedValue < min) {
          return;
        }
        onChange(parsedValue);
      }
    };

    const handleBlur = () => {
      setIsTyping(false);
      setTypingValue('');
      onBlur?.();
    };

    return (
      <TextField
        ref={reference}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        type='number'
        variant='outlined'
        size='small'
        fullWidth
        data-testid='number-input'
        slotProps={{
          htmlInput: { min, max },
          input: {
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (min !== undefined && min > 0 && event.key === '-') {
                event.preventDefault();
              }
              if (event.key === 'Enter' && onEnter) {
                onEnter();
              }
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: theme.palette.divider,
            },
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
        }}
        {...properties}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';
